import { Product, Seller } from '@/features/product/types/models/product';
import logger from '@/utils/logger';
import { create } from 'zustand';
import { ChatEventManager } from '../services/chatEvents';
import { ChatService } from '../services/chatService';
import { chatSocketService } from '../services/chatSocketService';
import { ChatListItem } from '../types/api';
import { ChatMessage, ChatState } from '../types/chat';

export const useChatStore = create<ChatState>((set, get) => ({
  // UI State
  isOverlayOpen: false,
  activeContactId: null,
  activeProductData: null,
  activeSellerData: null,

  // Connection State
  isConnected: false,
  isConnecting: false,

  // Data
  contacts: [],
  messages: {},
  unreadCounts: {},
  chatHistory: {},

  // UI Actions
  toggleOverlay: () => {
    set((state) => ({ isOverlayOpen: !state.isOverlayOpen, activeContactId: null }));
  },

  openChat: async (contactId: number) => {
    set({ activeContactId: contactId });
    get().markAsRead(contactId);

    try {
      await chatSocketService.joinRoom({ chatRoomId: contactId });
      const existingMessages = get().messages[contactId];

      if (!existingMessages || existingMessages.length === 0) {
        await get().loadMessages(contactId);
      }
    } catch (error) {
      logger.error('Failed to open chat:', error);
    }
  },

  closeChat: () => {
    set({ activeContactId: null });
  },

  setActiveProductData: (productData: Product | null) => {
    set({ activeProductData: productData });
  },

  setActiveSellerData: (sellerData: Seller | null) => {
    set({ activeSellerData: sellerData });
  },

  sendMessage: async (contactId: number, message: string) => {
    const optimisticMessage = await ChatService.createOptimisticMessage(message);
    get().addMessageToChat(contactId, optimisticMessage);

    try {
      await chatSocketService.sendMessage({
        content: message,
        chatRoomId: contactId,
      });

      if (optimisticMessage.tempId) {
        get().updateMessageStatus(contactId, optimisticMessage.tempId, 'sent');
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      if (optimisticMessage.tempId) {
        get().updateMessageStatus(contactId, optimisticMessage.tempId, 'failed');
      }
    }
  },

  markAsRead: (contactId: number, messageIds?: number[]) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [contactId]: 0,
      },
      contacts: state.contacts.map((contact) =>
        contact.id === contactId ? { ...contact, unreadCount: 0 } : contact
      ),
      messages: {
        ...state.messages,
        [contactId]: (state.messages[contactId] || []).map((msg) =>
          !msg.isOwn && (!messageIds || messageIds.includes(msg.id))
            ? { ...msg, isRead: true }
            : msg
        ),
      },
    }));

    if (chatSocketService.isConnected()) {
      chatSocketService.markMessagesAsRead({ chatRoomId: contactId, messageIds });
    }
  },

  // Data Loading
  loadContacts: async () => {
    try {
      await chatSocketService.getMyChats();
    } catch (error) {
      logger.error('Failed to load contacts:', error);
    }
  },

  loadMessages: async (contactId: number, page = 1) => {
    try {
      await chatSocketService.getChatHistory({ chatRoomId: contactId, page, limit: 10 });
    } catch (error) {
      logger.error('Failed to load messages:', error);
    }
  },

  startChat: async (targetUserEmail: string, productData?: Product, sellerData?: Seller): Promise<void> => {
    try {
      if (!productData) {
        logger.warn('No product data provided for startChat');
        return;
      }

      const newProductData = { ...productData, productId: productData.id };
      await chatSocketService.startChat({ targetUserEmail, productData: newProductData });

      if (sellerData) {
        set({ activeSellerData: sellerData });
      }
    } catch (error) {
      logger.error('Failed to start direct chat:', error);
    }
  },

  // Socket Connection
  connectSocket: async () => {
    const state = get();
    if (state.isConnected || state.isConnecting) {
      return;
    }

    set({ isConnecting: true });

    try {
      await chatSocketService.connect();
      set({ isConnected: true, isConnecting: false });

      await get().setupSocketListeners();

      await chatSocketService.subscribeToMyChats();
    } catch (error) {
      set({ isConnected: false, isConnecting: false });
      logger.error('Failed to connect socket:', error);
      throw error;
    }
  },

  disconnectSocket: () => {
    chatSocketService.disconnect();
    set({ isConnected: false, isConnecting: false });
  },

  // Message Management
  addMessageToChat: (chatId: number, message: ChatMessage) => {
    set((state) => {
      const existingContact = state.contacts.find(contact => contact.id === chatId);

      return {
        messages: {
          ...state.messages,
          [chatId]: [...(state.messages[chatId] || []), message],
        },
        contacts: existingContact
          ? state.contacts.map((contact) =>
            contact.id === chatId
              ? { ...contact, lastMessage: message.message, lastMessageTime: message.timestamp }
              : contact
          )
          : state.contacts,
      };
    });
  },

  updateMessageStatus: (chatId: number, tempId: string, status: 'sent' | 'failed') => {
    set((state) => ({
      messages: {
        ...state.messages,
        [chatId]: (state.messages[chatId] || []).map((msg) =>
          msg.tempId === tempId ? { ...msg, status } : msg
        ),
      },
    }));
  },

  replaceTemporaryMessage: (chatId: number, tempId: string, realMessage: ChatMessage) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [chatId]: (state.messages[chatId] || []).map((msg) =>
          msg.tempId === tempId ? realMessage : msg
        ),
      },
    }));
  },

  updateContactsFromServer: async (contactsData: ChatListItem[]) => {
    const { contacts, messages, chatHistory } = await ChatService.convertContactsFromServer(contactsData);

    set((state) => ({
      contacts,
      messages: { ...state.messages, ...messages },
      chatHistory: { ...state.chatHistory, ...chatHistory },
    }));
  },

  updateMessagesFromHistory: (chatRoomId: number, messages: ChatMessage[], total: number, hasMore: boolean, page: number) => {
    set((state) => {
      const existingMessages = state.messages[chatRoomId] || [];
      let updatedMessages: ChatMessage[];

      if (page === 1) {
        updatedMessages = messages;
      } else {
        const filteredMessages = ChatService.filterUniqueMessages(messages, existingMessages);
        updatedMessages = [...filteredMessages, ...existingMessages];
      }

      return {
        messages: {
          ...state.messages,
          [chatRoomId]: updatedMessages,
        },
        chatHistory: {
          ...state.chatHistory,
          [chatRoomId]: { page, hasMore, total, isLoadingMore: false },
        },
      };
    });
  },

  // Socket Event Setup
  setupSocketListeners: async () => {
    await ChatEventManager.setupSocketListeners(get, set);
  },

  // Infinite Scroll
  loadMoreMessages: async (contactId: number): Promise<void> => {
    const state = get();
    const history = state.chatHistory[contactId];

    if (!history || !history.hasMore || history.isLoadingMore) {
      return;
    }

    set((state) => ({
      chatHistory: {
        ...state.chatHistory,
        [contactId]: { ...history, isLoadingMore: true },
      },
    }));

    try {
      const existingMessages = state.messages[contactId] || [];
      const beforeMessageId = existingMessages.length > 0 ? existingMessages[0].id : undefined;

      await chatSocketService.getChatHistory({
        chatRoomId: contactId,
        limit: 10,
        beforeMessageId,
      });

    } catch (error) {
      logger.error('Failed to load more messages:', error);
      set((state) => ({
        chatHistory: {
          ...state.chatHistory,
          [contactId]: { ...state.chatHistory[contactId], isLoadingMore: false },
        },
      }));
    }
  },
}));